import React, { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { Menu } from 'primereact/menu';
import { Tooltip } from 'primereact/tooltip';
import { useUserDetailsContext } from 'context/userDetailsContext';
import LabelName from 'components/UI/LabelName/LabelName';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import TimezoneDatePickerWithTime from 'components/TimezoneDatePickerWithTime/TimezoneDatePickerWithTime';
import { getTimeInMinutes } from 'utils/dateTimeUtils';

function EventTime({ control, trigger, errors, setValue, watch, getValues }) {
  const { userDetails } = useUserDetailsContext();
  const startTimeWatcher = watch('meeting_start_time');
  const addTimePanel = useRef(null);

  // Menu items for quick duration selection
  const items = [
    {
      label: '30 Minutes',
      command: () => {
        addTimeHandler(startTimeWatcher, 30, 'meeting_end_time');
      },
    },
    {
      label: '1 Hour',
      command: () => {
        addTimeHandler(startTimeWatcher, 60, 'meeting_end_time');
      },
    },
    {
      label: '2 Hours',
      command: () => {
        addTimeHandler(startTimeWatcher, 120, 'meeting_end_time');
      },
    },
  ];

  function addTimeHandler(time, extraTime, field) {
    if (time) {
      let new_time = new Date(time);
      let addedTime = new_time.setMinutes(new_time.getMinutes() + extraTime);
      setValue(field, addedTime);
      trigger('meeting_end_time');
    }
  }

  return (
    <div className="d-flex align-items-center py-2 flex-wrap">
      <div className="col-md-4 col-12">
        <LabelName required={true}>Time</LabelName>
      </div>
      <div className="col-md-8 col-12 d-flex justify-content-between align-items-center">
        {/* Start Time */}
        <div className="w-50">
          <Controller
            name="meeting_start_time"
            control={control}
            rules={{ required: 'Please select start time' }}
            render={({ field }) => (
              <TimezoneDatePickerWithTime
                id={field.name}
                value={field.value}
                onChange={(e) => {
                  const newStartTime = e.target.value;
                  field.onChange(newStartTime);
                  trigger('meeting_start_time');

                  // Auto-set end time to 30 minutes after start time
                  const endTime = new Date(newStartTime);
                  endTime.setMinutes(endTime.getMinutes() + 30);
                  setValue('meeting_end_time', endTime);
                }}
                timeOnly
                hourFormat="12"
                stepMinute={30}
                placeholder="Start Time"
                className="input-shadow w-100"
                inputRef={field.ref}
                timezone={userDetails?.timezone}
              />
            )}
          />
        </div>

        {/* End Time */}
        <div className="w-50 me-2">
          <Controller
            name="meeting_end_time"
            control={control}
            rules={{
              required: 'Please select end time',
              validate: (value) => {
                const startTime = getValues('meeting_start_time');
                if (!startTime || !value) return true;

                const startMinutes = getTimeInMinutes(startTime, userDetails?.timezone);
                const endMinutes = getTimeInMinutes(value, userDetails?.timezone);

                if (endMinutes <= startMinutes) {
                  return 'End time must be after start time';
                }
                return true;
              },
            }}
            render={({ field }) => (
              <TimezoneDatePickerWithTime
                id={field.name}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e.target.value);
                  // trigger('meeting_end_time');
                }}
                timeOnly
                hourFormat="12"
                stepMinute={30}
                placeholder="End Time"
                className="input-shadow w-100 mx-2"
                inputRef={field.ref}
                timezone={userDetails?.timezone}
              />
            )}
          />
        </div>

        {/* Quick Duration Selector */}
        {startTimeWatcher && (
          <>
            <Tooltip position="right" target="#duration" content="Time Duration" style={{ marginLeft: '10px' }} />
            <i
              id="duration"
              label="+"
              className="ms-3 fas fa-plus-circle pointer"
              onClick={(event) => addTimePanel.current.toggle(event)}
              aria-controls="add_time_panel"
              aria-haspopup
            />
            <Menu model={items} popup ref={addTimePanel} id="add_time_panel" />
          </>
        )}
      </div>
      <div className="offset-md-4">
        {errors?.meeting_start_time && <InputErrorMessage>{errors.meeting_start_time.message}</InputErrorMessage>}
        {errors?.meeting_end_time && <InputErrorMessage>{errors.meeting_end_time.message}</InputErrorMessage>}
      </div>
    </div>
  );
}

export default EventTime;
